import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
} from '../api/client';

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  body: '',
  cta_label: '',
  cta_url: '',
  discount_percent: null,
  old_price_local: '',
  new_price_local: '',
  old_price_intl: '',
  new_price_intl: '',
  starts_at: '',
  ends_at: '',
  is_active: true,
};

function toInputDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function fromInputDate(s) {
  if (!s) return null;
  return new Date(s).toISOString();
}

function Field({ label, children, full = false }) {
  return (
    <label className={`block ${full ? 'col-span-2' : ''}`}>
      <span className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none';

function PromotionModal({ promotion, onClose, onSave }) {
  const isEdit = !!promotion?.id;
  const [form, setForm] = useState(() => {
    if (!promotion?.id) return { ...EMPTY_FORM };
    return {
      ...EMPTY_FORM,
      ...promotion,
      starts_at: toInputDate(promotion.starts_at),
      ends_at: toInputDate(promotion.ends_at),
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: v }));
  };

  const setNum = (key) => (e) => {
    const v = e.target.value;
    setForm((p) => ({ ...p, [key]: v === '' ? null : Number(v) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Sarlavha kiritilishi shart');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: fromInputDate(form.starts_at),
        ends_at: fromInputDate(form.ends_at),
      };
      if (isEdit) {
        await updatePromotion(promotion.id, payload);
        toast.success('Aksiya yangilandi');
      } else {
        await createPromotion(payload);
        toast.success('Aksiya yaratildi');
      }
      onSave();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Saqlab bo'lmadi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold">{isEdit ? 'Aksiyani tahrirlash' : 'Yangi aksiya'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            ✕
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Field label="Sarlavha *" full>
            <input className={inputCls} value={form.title} onChange={set('title')} placeholder="Masalan: Yozgi aksiya — 40% chegirma" />
          </Field>
          <Field label="Podsarlavha" full>
            <input className={inputCls} value={form.subtitle || ''} onChange={set('subtitle')} placeholder="Qo'shimcha tavsif" />
          </Field>
          <Field label="Matn (body)" full>
            <textarea rows={3} className={inputCls} value={form.body || ''} onChange={set('body')} />
          </Field>

          <Field label="CTA yozuvi">
            <input className={inputCls} value={form.cta_label || ''} onChange={set('cta_label')} placeholder="Aksiyadan foydalanish" />
          </Field>
          <Field label="CTA URL">
            <input className={inputCls} value={form.cta_url || ''} onChange={set('cta_url')} placeholder="/pages/submit.html" />
          </Field>

          <Field label="Chegirma %">
            <input type="number" min="0" max="100" className={inputCls} value={form.discount_percent ?? ''} onChange={setNum('discount_percent')} />
          </Field>
          <Field label="Faol">
            <label className="inline-flex items-center gap-2 mt-2">
              <input type="checkbox" checked={!!form.is_active} onChange={set('is_active')} />
              <span className="text-sm">Publicda ko'rsatiladi</span>
            </label>
          </Field>

          <Field label="Eski narx (mahalliy)">
            <input className={inputCls} value={form.old_price_local || ''} onChange={set('old_price_local')} placeholder="250 000 so'm" />
          </Field>
          <Field label="Yangi narx (mahalliy)">
            <input className={inputCls} value={form.new_price_local || ''} onChange={set('new_price_local')} placeholder="150 000 so'm" />
          </Field>

          <Field label="Eski narx (xalqaro)">
            <input className={inputCls} value={form.old_price_intl || ''} onChange={set('old_price_intl')} placeholder="20 USD" />
          </Field>
          <Field label="Yangi narx (xalqaro)">
            <input className={inputCls} value={form.new_price_intl || ''} onChange={set('new_price_intl')} placeholder="12 USD" />
          </Field>

          <Field label="Boshlanishi">
            <input type="datetime-local" className={inputCls} value={form.starts_at} onChange={set('starts_at')} />
          </Field>
          <Field label="Tugashi">
            <input type="datetime-local" className={inputCls} value={form.ends_at} onChange={set('ends_at')} />
          </Field>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
            Bekor qilish
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saqlanmoqda...' : 'Saqlash'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PromotionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPromotions();
      setItems(res.data || []);
    } catch (e) {
      toast.error("Ro'yxatni yuklab bo'lmadi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Ushbu aksiyani o'chirishni tasdiqlaysizmi?")) return;
    try {
      await deletePromotion(id);
      toast.success("O'chirildi");
      load();
    } catch (e) {
      toast.error("O'chirib bo'lmadi");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('uz-UZ', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Aksiyalar</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Public fees sahifasida ko'rsatiladigan marketing banner va narx chegirmalari.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
        >
          + Yangi aksiya
        </button>
      </div>

      {loading ? (
        <p className="text-slate-500">Yuklanmoqda...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl">
          <p className="text-slate-500">Hozircha aksiyalar yo'q</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-left">
              <tr>
                <th className="px-4 py-3">Sarlavha</th>
                <th className="px-4 py-3">Chegirma</th>
                <th className="px-4 py-3">Boshlanishi</th>
                <th className="px-4 py-3">Tugashi</th>
                <th className="px-4 py-3">Holat</th>
                <th className="px-4 py-3 text-right">Amal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.title}</div>
                    {it.subtitle && <div className="text-slate-500 text-xs">{it.subtitle}</div>}
                  </td>
                  <td className="px-4 py-3">{it.discount_percent ? `-${it.discount_percent}%` : '—'}</td>
                  <td className="px-4 py-3">{formatDate(it.starts_at)}</td>
                  <td className="px-4 py-3">{formatDate(it.ends_at)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        'inline-flex items-center px-2 py-1 text-xs rounded-full ' +
                        (it.is_active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400')
                      }
                    >
                      {it.is_active ? 'Faol' : 'Nofaol'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => {
                        setEditing(it);
                        setModalOpen(true);
                      }}
                      className="mr-2 text-blue-600 hover:underline"
                    >
                      Tahrirlash
                    </button>
                    <button onClick={() => handleDelete(it.id)} className="text-red-600 hover:underline">
                      O'chirish
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <PromotionModal
          promotion={editing}
          onClose={() => setModalOpen(false)}
          onSave={load}
        />
      )}
    </div>
  );
}
